/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { ExcpPartnerUserSimpleSearchCondition } from 'models/admin/ExcpPartner';
import { getSimpleExcpUserList } from 'apis/admin/ExcpPartner';
import { ExcpPartnerUser } from 'models/admin/ExcpPartner';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import SearchBoxButtons from '../../buttons/SearchBoxButtons';

type Props = {
  open: boolean;
  close: () => void;
  condition?: {
    userId?: string;
    userNm?: string;
    excpId?: string;
    excpNm?: string;
  };
  selectedIds?: string[]; // 선택된 협력사담당자ID (userId)
  onCallback: (item: any) => void;
  singleSelect?: boolean;
  isFixExcp?: boolean;
};

const ExcpUserModal = ({
  open,
  close,
  condition,
  onCallback,
  singleSelect = true,
  selectedIds = [],
  isFixExcp = false,
}: Props) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const [rowData, setRowData] = useState<ExcpPartnerUser[]>([]);
  const [partnerCondition, setPartnerCondition] = useState<ExcpPartnerUserSimpleSearchCondition>({
    userId: condition?.userId || '',
    userNm: condition?.userNm || '',
    excpId: condition?.excpId || '',
    excpNm: condition?.excpNm || '',
    useYn: 'Y',
  });

  const [colWidthMap, setColWidthMap] = useState<any>({
    userId: 359,
    userNm: 180,
    ptnrNm: '*',
    useYn: 95,
  });

  useEffect(() => {
    if (
      !_.isEmpty(condition?.userId) ||
      !_.isEmpty(condition?.userNm) ||
      !_.isEmpty(condition?.excpNm) ||
      !_.isEmpty(condition?.excpId)
    ) {
      handleSearch(partnerCondition);
    }
  }, []);

  useEffect(() => {
    if ((gridRef.current?.rows || []).length) {
      if ((selectedIds || []).length > 0) {
        gridRef.current?.rows.map((o, idx) => {
          o.isSelected = selectedIds.includes(o.dataItem.userId);
        });
      }
    }
  }, [rowData]);

  const handleButtonClick = (params) => {
    onCallback(params);
    close();
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setPartnerCondition({ ...partnerCondition, [e.target.name]: e.target.value });
  };

  const layoutDefinition = [
    {
      binding: 'btnSelect',
      header: ' ',
      width: 39,
      cssClass: 'WijmoRelay',
      cellTemplate: '<Button />',
    },
    {
      binding: 'userId',
      header: String(t('com.label.사용자ID', '사용자ID')),
      isReadOnly: true,
      width: colWidthMap.userId,
      align: 'left',
    },
    {
      binding: 'userNm',
      header: String(t('com.label.사용자명', '사용자명')),
      isReadOnly: true,
      width: colWidthMap.userNm,
      align: 'center',
    },
    {
      binding: 'excpNm',
      header: String(t('com.label.협력업체명', '협력업체명')),
      isReadOnly: true,
      width: colWidthMap.ptnrNm,
      align: 'left',
    },
    {
      binding: 'useYn',
      header: String(t('com.label.사용여부', '사용여부')),
      isReadOnly: true,
      width: colWidthMap.useYn,
      align: 'center',
    },
  ];

  const onInitialized = (grid) => {
    gridRef.current = grid;

    //columnGroups속성 사용할 경우 -> Column 너비 수동 지정
    grid.resizingColumn.addHandler((s, e) => {
      const cell = s.columnHeaders.getCellElement(0, e.col);
      const col = e.panel.columns[e.col];
      colWidthMap[col.binding] = col.width;
      setColWidthMap(colWidthMap);
    });

    grid.hostElement.addEventListener('dblclick', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;

      if (item) {
        handleButtonClick(item);
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const binding = grid.columns[ht.col].binding;
      const item = grid.rows[ht.row].dataItem;

      if (ht.panel === grid.cells) {
        // Quick Select
        if ('btnSelect' === binding) {
          handleButtonClick(item);
        }
      }
    });
  };

  const handleSearch = async (condition: ExcpPartnerUserSimpleSearchCondition) => {
    getSimpleExcpUserList(condition).then((result) => {
      setRowData(result);
    });
  };

  const handleResetCondition = () => {
    setPartnerCondition(
      (prev) =>
        Object.keys(prev).reduce(
          (acc, cur) =>
            Object.assign(acc, {
              [cur]: cur === 'useYn' ? 'Y' : '',
            }),
          {}
        ) as ExcpPartnerUserSimpleSearchCondition
    );
  };

  const handleConfirm = () => {
    const checkedItems = gridRef.current.rows.filter((o) => o.isSelected).map((o) => o.dataItem);
    if (singleSelect) {
      onCallback && onCallback((checkedItems || []).length ? checkedItems[0] : null);
    } else {
      onCallback && onCallback(checkedItems);
    }
    close();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(partnerCondition);
    }
  };

  const dialogButtons = [
    <Button
      key="confirm"
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];

  return (
    <CustomDialog
      title={t('com.label.협력사 담당자 조회', '협력사 담당자 조회')}
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="popup">
              <SearchCols>
                <label>{t('com.label.사용자ID', '사용자ID')}</label>
                <CustomInputWithSearch
                  name="userId"
                  placeholder={String(
                    t('com.label.사용자 ID를 입력해 주세요.', '사용자 ID를 입력해 주세요.')
                  )}
                  value={partnerCondition.userId}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.사용자명', '사용자명')}</label>
                <CustomInputWithSearch
                  name="userNm"
                  placeholder={String(
                    t('com.label.사용자명을 입력해 주세요.', '사용자명을 입력해 주세요.')
                  )}
                  value={partnerCondition.userNm}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              {!isFixExcp && (
                <SearchCols>
                  <label>{t('com.label.협력업체명', '협력업체명')}</label>
                  <CustomInputWithSearch
                    name="excpNm"
                    placeholder={String(
                      t('com.label.협력업체명을 입력해 주세요.', '협력업체명을 입력해 주세요.')
                    )}
                    value={partnerCondition.excpNm}
                    readOnly={!!condition?.userId}
                    onChange={handleConditionChange}
                    onKeyDown={handleKeyDown}
                  />
                </SearchCols>
              )}
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            onReset={handleResetCondition}
            onSearch={() => handleSearch(partnerCondition)}
          />
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('com.label.사용자', '사용자')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
      </SubTitleLayout>

      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={480}
        isReadOnly={true}
        allowPinning={false}
        autoCheck={true}
        rowSelection={singleSelect ? 'single' : 'multiple'}
        excludeFilter={['btnSelect', 'useYn']}
        initialized={onInitialized}
      />
    </CustomDialog>
  );
};

export default ExcpUserModal;
